import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import GiftCard from '../../../components/GiftCard';
import SuccessCircle from '../../../components/SuccessCircle';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button';
import ScrollRestore from '../../../components/ScrollRestore';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openModal } from '../../../redux/slices/modals';
import { selectThemeMode } from '../../../redux/slices/themeSlice';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  min-height: 500px;
  gap: 48px;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px 0;
    gap: 24px;
    min-height: unset;
  }
`;

const CardContainer = styled.div`
  max-width: 500px;
  width: 100%;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 20px;
`;

// Animation for the loading text
const pulseAnimation = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

// Base styles for the status text
const StatusText = styled.h3`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 20px;
  text-align: center;
  margin-top: 8px;
  transition: color 0.3s ease;

  /* Different styling based on success/loading state */
  color: ${({ theme, isError }) =>
    isError ? `${theme.status.error.primary}` : `${theme.text.title}`};

  /* Use the css helper to conditionally apply the animation */
  ${({ isSuccess }) =>
    isSuccess
      ? css``
      : css`
          animation: ${pulseAnimation} 2s infinite ease-in-out;
        `}
`;

const Email = styled.span`
  font-weight: 600;
  word-break: break-all;
  color: ${({ theme }) => theme.primary.primary};
  display: block;
  margin-top: 4px;
`;

const StatusIcon = styled.div`
  margin: 10px 0;
`;

const ErrorCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.status.error.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.background.primary};
  font-size: 24px;
  font-weight: bold;
`;

export const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: unset;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background-color: ${({ theme }) => theme.primary.lightest};
  border-radius: 999px;
  min-width: 516px;
  @media ${PHONE_MEDIA_QUERY} {
    min-width: unset;
    width: calc(100vw - 70px);
  }
`;

const ProgressBar = styled.div`
  width: ${(props) => props.progress}%;
  height: 100%;
  border-radius: 999px;
  background-color: ${({ theme, isSuccess }) =>
    isSuccess ? theme.status.success.primary : theme.primary.primary};
  transition:
    width 0.5s ease-in-out,
    background-color 0.3s ease;
`;

const ProgressBarLoading = ({ isSuccess = false }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isSuccess) {
      setProgress(100);
      return;
    }

    setProgress(10);
    const timeouts = [];

    timeouts.push(
      setTimeout(() => {
        setProgress(20);
      }, 1000),
    );
    timeouts.push(
      setTimeout(() => {
        setProgress(30);
      }, 4000),
    );
    timeouts.push(
      setTimeout(() => {
        setProgress(30);
      }, 4000),
    );
    timeouts.push(
      setTimeout(() => {
        setProgress(50);
      }, 8000),
    );
    timeouts.push(
      setTimeout(() => {
        setProgress(70);
      }, 10000),
    );
    timeouts.push(
      setTimeout(() => {
        setProgress(95);
      }, 12000),
    );

    // Cleanup function to clear timeouts
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [isSuccess]);

  return (
    <ProgressBarContainer>
      <ProgressBar progress={progress} isSuccess={isSuccess} />
    </ProgressBarContainer>
  );
};

const LOADING_PHRASES = [
  'Claiming your gift...',
  'Unwrapping your gift...',
  'Summoning the blockchain gods...',
  'Decrypting happiness...',
];

/**
 * SendingStatus component - displays the loading state while sending a gift
 *
 * @param {Object} props
 * @param {string} [props.email] - Recipient's email (optional)
 * @param {string} [props.giftUrl] - Gift URL for sharing (optional)
 * @param {Object} props.token - Selected token
 * @param {string} props.usdAmount - USD amount
 * @param {string} props.tokenAmount - Token amount
 * @param {string} props.cardImage - Card image
 * @param {boolean} [props.isSuccess] - Whether sending was successful
 * @param {boolean} [props.isError] - Whether there was an error
 * @param {string} [props.successText] - Custom success text (defaults to "Gift sent to")
 * @param {string} [props.errorText] - Custom error text (defaults to "Failed to send gift")
 * @param {string} [props.loadingText] - Custom loading text (defaults to "Sending to")
 * @param {string} [props.successButtonText] - Custom button text (defaults to "Go to wallet")
 * @param {string} [props.errorButtonText] - Custom button text for error (defaults to "Try Again")
 * @param {Function} [props.successAction] - Custom action for success button (defaults to navigating to wallet)
 * @param {Function} [props.errorAction] - Custom action for error button
 */
const SendingStatus = ({
  email,
  giftUrl,
  token,
  cardImage,
  usdAmount,
  tokenAmount,
  isSuccess = false,
  isError = false,
  successText = 'Gift sent to',
  errorText = 'Failed to send gift',
  loadingText = 'Sending to',
  successButtonText = 'Go to wallet',
  errorButtonText = 'Try Again',
  successAction,
  errorAction,
}) => {
  const navigate = useNavigate();

  const [localLoadingText, setLocalLoadingText] = useState(loadingText);

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const selectedTheme = useSelector(selectThemeMode);

  const dispatch = useDispatch();

  useEffect(() => {
    let currentIndex = 0;
    if (loadingText === 'Claiming gift...') {
      setLocalLoadingText(LOADING_PHRASES[currentIndex]);
      currentIndex++;
      const interval = setInterval(() => {
        setLocalLoadingText(LOADING_PHRASES[currentIndex]);
        currentIndex++;
        if (currentIndex >= LOADING_PHRASES.length) {
          currentIndex = 0;
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [loadingText]);

  const handleSuccessAction = () => {
    dispatch(closeModal());
    if (successAction) {
      successAction();
    } else {
      navigate('/app/home');
    }
  };

  const handleErrorAction = () => {
    if (errorAction) {
      errorAction();
    }
  };

  const handleClickShare = () => {
    dispatch(openModal({ name: 'shareGift', props: { claimUrl: giftUrl } }));
  };

  return (
    <>
      <ScrollRestore />
      <Container>
        <CardContainer>
          <GiftCard
            token={token}
            usdAmount={usdAmount}
            tokenAmount={tokenAmount}
            cardImage={cardImage}
          />
        </CardContainer>

        <StatusContainer>
          {!isError && <ProgressBarLoading isSuccess={isSuccess} />}
          {isError && (
            <StatusIcon>
              <ErrorCircle>✗</ErrorCircle>
            </StatusIcon>
          )}
          <StatusText isSuccess={isSuccess} isError={isError}>
            {isSuccess ? (
              successText === 'Gift sent to' ? (
                email ? (
                  <>
                    Gift sent to
                    <br />
                    <Email>{email}</Email>
                  </>
                ) : (
                  <>Gift created successfully</>
                )
              ) : (
                <>
                  {email &&
                    successText.split(email).map((part, index, array) => (
                      <React.Fragment key={index}>
                        {part}
                        {index < array.length - 1 && <Email>{email}</Email>}
                      </React.Fragment>
                    ))}
                  {!email && successText}
                </>
              )
            ) : isError ? (
              errorText === 'Failed to send gift' ? (
                email ? (
                  <>
                    Failed to send gift to
                    <br />
                    <Email>{email}</Email>
                  </>
                ) : (
                  <>Failed to create gift</>
                )
              ) : (
                errorText
              )
            ) : loadingText === 'Sending to' ? (
              email ? (
                <>
                  Sending to
                  <br />
                  <Email>{email}</Email>
                </>
              ) : (
                <>Creating gift...</>
              )
            ) : (
              localLoadingText
            )}
          </StatusText>
        </StatusContainer>
        <ButtonContainer>
          {isSuccess &&
            giftUrl &&
            (!Capacitor.isNativePlatform() ||
              !Capacitor.isPluginAvailable('Share')) && (
              <StyledButton fullWidth={isMobile} onClick={handleClickShare}>
                <img
                  src='/share.svg'
                  alt='share'
                  style={{
                    marginRight: 8,
                    filter: `brightness(0) invert(${selectedTheme === 'dark' ? 0 : 1})`,
                  }}
                />
                Share
              </StyledButton>
            )}
          {isSuccess &&
            giftUrl &&
            Capacitor.isNativePlatform() &&
            Capacitor.isPluginAvailable('Share') && (
              <StyledButton
                fullWidth={isMobile}
                onClick={() => {
                  Share.share({
                    url: giftUrl,
                  });
                }}
              >
                Share Link
              </StyledButton>
            )}
          {isSuccess && (
            <StyledButton
              variant='secondaryLight'
              fullWidth={isMobile}
              onClick={handleSuccessAction}
            >
              {successButtonText}
            </StyledButton>
          )}
          {isError && errorAction && (
            <StyledButton fullWidth={isMobile} onClick={handleErrorAction}>
              {errorButtonText}
            </StyledButton>
          )}
        </ButtonContainer>
      </Container>
    </>
  );
};

export default SendingStatus;
